import vuetifyToast from 'vuetify-toast'
import { baseActions, baseMutations } from '../base'
import { apiAxios, parseFilters, parseSliderFilters } from '@/helpers'
import { formatDate } from '@/helpers/dateHelper'
import { getMatchFilters } from '@/helpers/permissions'
import i18n from '@/i18n/i18n'

const CACHE_BUSTER_PARAM = '_cb'

const stripCacheBuster = (src = '') => {
  if (!src || !src.includes(`${CACHE_BUSTER_PARAM}=`)) return src

  const [base, hash = ''] = src.split('#')
  const parts = base.split('?')
  if (parts.length === 1) return src

  const query = parts[1]
    .split('&')
    .filter((part) => part && !part.startsWith(`${CACHE_BUSTER_PARAM}=`))
    .join('&')

  const clean = query ? `${parts[0]}?${query}` : parts[0]
  return hash ? `${clean}#${hash}` : clean
}

const hasAwsSignature = (src = '') =>
  /X-Amz-Algorithm|X-Amz-Credential|X-Amz-Signature/i.test(src)

const isYoutubeVideo = (video = {}) => {
  const src = video ? video.src || '' : ''
  return video.type === 'video/youtube' || /youtu(\.be|be\.com)/i.test(src)
}

const appendCacheBuster = (src = '', cacheKey) => {
  if (!src || !cacheKey || hasAwsSignature(src)) return src

  const [base, hash = ''] = stripCacheBuster(src).split('#')
  const separator = base.includes('?') ? '&' : '?'
  const withParam = `${base}${separator}${CACHE_BUSTER_PARAM}=${cacheKey}`

  return hash ? `${withParam}#${hash}` : withParam
}

const buildCacheKey = (video) =>
  (video &&
    (video.updatedAt ||
      video.updated ||
      video.modifiedAt ||
      video.lastModified ||
      video.createdAt)) ||
  Date.now()

const cacheBustVideo = (video, previousVideo) => {
  if (!video || isYoutubeVideo(video)) return video

  const previousSrc = previousVideo ? stripCacheBuster(previousVideo.src) : ''
  const currentSrc = stripCacheBuster(video.src)

  const cacheKey =
    previousSrc && currentSrc && previousSrc === currentSrc
      ? `${buildCacheKey(video)}-${Date.now()}`
      : buildCacheKey(video)

  if (Array.isArray(video.sources)) {
    return {
      ...video,
      sources: video.sources.map((source) => ({
        ...source,
        src: appendCacheBuster(source.src, cacheKey),
      })),
      src: appendCacheBuster(video.src, cacheKey),
    }
  }

  return {
    ...video,
    src: appendCacheBuster(video.src, cacheKey),
  }
}
const notify = (message) => {
  vuetifyToast.show({
    text: message,
    color: 'success',
    timeout: 2000,
    icon: 'check',
  })
}
const paginate = () => ({
  hasMore: false,
  page: 1,
  rowsPerPage: 5,
  totalItems: 0,
})
const sliderTemplate = () => ({
  listObject: [],
  paginate: paginate(),
  filters: {},
  icon: '',
})
// import moment from 'moment'

const initialState = () => {
  return {
    module: 'trainings',
    model: 'training',
    loading: false,
    list: [],
    simplified: {},
    selectList: [],
    upcomingTrainings: sliderTemplate(),
    lastTrainings: sliderTemplate(),
    liveTrainings: sliderTemplate(),
    selected: {
      active: true,
      date: '', // moment().format('YYYY-MM-DD'),
      // location: '',
      name: '',
      description: '',
      teamId: '',
      trainingDuration: 90,
      recording: false,
      streamingAndRecording: true,
      autoStreaming: true,
    },
    idSelected: '',
    video: null,
    types: ['league', 'tournament', 'friendly'],
    filters: {},
    pagination: {
      hasMore: false,
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    },
  }
}

const state = initialState()

const getters = {
  matchTypes(state) {
    return state.types.map((item) => ({
      value: item,
      text: i18n.t(`fields.global.types.${item}`),
    }))
  },
  selectList(state) {
    let activeList = state.list.filter((item) => item.active)
    return activeList.map((item) => {
      let result = {
        value: item._id,
        text: item.name,
      }
      return result
    })
  },
  formatedList(state) {
    return state.list.map((item) => {
      let result = {
        ...item,
        date: formatDate(item.date),
        createdAt: formatDate(item.createdAt, true),
      }
      return result
    })
  },
}

const actions = {
  ...baseActions,
  async getOne({ commit, state }, id) {
    commit('setLoading', true)
    const { data } = await apiAxios
      .post(`${state.module}/${id}`)
      .finally(() => commit('setLoading', false))
    const item = data.data ? data.data[state.model] : {}
    return item
  },
  async fetchOne({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    const { data } = await apiAxios
      .post(`${state.module}/${id}`)
      .finally(() => commit('setLoading', false))

    commit('setLoading', true)
    let promises = [
      dispatch('fetchEvents', id),
      dispatch(
        'typeEvents/fetchSelectList',
        { type: ['training', 'user'] },
        { root: true }
      ),
    ]

    promises.push(dispatch('getTrainingVideo', id))
    Promise.all(promises).finally(() => commit('setLoading', false))

    const item = data.data ? data.data[state.model] : {}
    commit('setOne', item)
    return item
  },
  async fetchSimplified({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    const { data } = await apiAxios
      .post(`${state.module}/${id}/simplified`)
      .finally(() => commit('setLoading', false))

    Promise.all([
      dispatch('getTrainingVideo', id),
      dispatch('fetchEvents', id),
      dispatch(
        'typeEvents/fetchSelectList',
        { type: ['user', 'training'] },
        { root: true }
      ),
    ]).finally(() => commit('setLoading', false))

    const item = data.data ? data.data[state.model] : {}
    commit('setSimplified', item)
    return item
  },
  async fetchEvents({ dispatch, commit, rootState }, id) {
    commit('setLoading', true)
    await dispatch(
      'matchEvents/fetchList',
      {
        trainingId: id,
        type: 'all',
        userId: rootState.auth.user._id,
        limit: 0,
      },
      { root: true }
    ).finally(() => commit('setLoading', false))
  },
  async fetchList({ commit, state }, params = {}) {
    let { body, query } = parseFilters(state, params, getMatchFilters(params))

    commit('setLoading', true)
    const { data } = await apiAxios
      .post(state.module + '/format', body, { params: { ...query } })
      .finally(() => commit('setLoading', false))

    commit('setPagination', {
      totalItems: data.paginate ? data.paginate.totalData : 0,
    })

    const list = data.data
      ? data.data[state.model] || data.data[state.module]
      : []
    commit('setList', list)
  },
  async startTraining({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    let { data } = await apiAxios
      .post(state.module + '/start/' + id)
      .finally(() => {
        commit('setLoading', false)
        dispatch('fetchOne', id).finally(() => {
          dispatch('fetchList')
        })
      })
    notify(data.message)
  },
  async endTraining({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    let { data } = await apiAxios
      .post(state.module + '/end/' + id)
      .finally(() => {
        commit('setLoading', false)
        dispatch('fetchOne', id).finally(() => {
          dispatch('fetchList')
        })
      })
    notify(data.message)
  },
  async startStreaming({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    let { data } = await apiAxios
      .post(state.module + '/start_streaming/' + id)
      .finally(() => {
        commit('setLoading', false)
        dispatch('fetchOne', id).finally(() => {
          commit('setVideo')
          setTimeout(() => {
            dispatch('getTrainingVideo', id)
          }, 10000)
        })
      })
    notify(data.message)
  },
  async endStreaming({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    let { data } = await apiAxios
      .post(state.module + '/end_streaming/' + id)
      .finally(() => {
        commit('setLoading', false)
        dispatch('fetchOne', id).finally(() => {
          dispatch('getTrainingVideo', id)
        })
      })
    notify(data.message)
  },
  async getTrainingVideo({ commit, state }, id) {
    commit('setLoading', true)
    const { data } = await apiAxios
      .get(state.module + '/video/' + id)
      .finally(() => commit('setLoading', false))
    const video = data.data ? data.data.video : null
    const nextVideo = cacheBustVideo(video, state.video)

    commit('setVideo')
    setTimeout(() => {
      commit('setVideo', nextVideo)
    }, 100)
  },
  async fetchSliders({ commit, state }, params = {}) {
    const slider = params.slider
    delete params.slider
    if (slider && !state[slider]) return

    let { body, query } = parseSliderFilters(
      state[slider].filters,
      state[slider].paginate,
      params,
      getMatchFilters(params)
    )
    query.admin = true
    if (slider) query.slider = slider

    const { data } = await apiAxios.post(state.module + '/all/sliders', body, {
      params: { ...query },
    })

    if (data.data && data.data.sliders && data.data.sliders.length) {
      data.data.sliders.forEach((slider) => {
        commit('setSlider', slider)
      })
    } else if (slider) {
      state[slider] = { ...sliderTemplate(), filters: state[slider].filters }
    }
  },
  async resetTraining({ dispatch, commit, state }, id) {
    commit('setLoading', true)
    let { data } = await apiAxios
      .post(state.module + '/reset/' + id)
      .finally(() => {
        commit('setLoading', false)
        dispatch('fetchOne', id).finally(() => {
          dispatch('fetchList')
        })
      })
    notify(data.message)
  },
}

const mutations = {
  ...baseMutations,
  setSimplified(state, payload) {
    if (payload && typeof payload === 'object') {
      state.simplified = payload
      state.idSelected = payload._id
    }
  },
  setSlider(state, payload) {
    if (!state[payload.typeSlider]) return

    if (payload && typeof payload === 'object') {
      // List
      if (Array.isArray(payload.listObject)) {
        state[payload.typeSlider].listObject = []
        payload.listObject.forEach((item) =>
          state[payload.typeSlider].listObject.push({
            ...item,
            date: formatDate(item.date),
          })
        )
      }
      // Icon
      state[payload.typeSlider].icon = payload.icon
      state[payload.typeSlider].paginate = {
        ...state[payload.typeSlider].paginate,
        page: payload.paginate.actualPage,
        hasMore: payload.paginate.hasMore,
        totalItems: payload.paginate.totalData,
      }
    }
  },
  setSliderFilters(state, payload) {
    if (payload && state[payload.slider]) {
      const slider = payload.slider
      delete payload.slider
      state[slider].filters = payload
    }
  },
  setSliderPagination(state, payload) {
    if (payload && state[payload.slider]) {
      const slider = payload.slider
      delete payload.slider
      state[slider].paginate = Object.assign(state[slider].paginate, payload)
    }
  },
  resetState(state) {
    const newState = initialState()
    Object.keys(state).forEach((key) => {
      delete state[key]
    })
    Object.assign(state, newState)
  },
  reset(state) {
    state.selected = initialState().selected
    state.idSelected = ''
    state.video = null
    state.simplified = {}
  },
  setVideo(state, payload) {
    state.video = payload
  },
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
}
